import * as purchaseProcess from './transactionProcessPurchase';
import * as bookingProcess from './transactionProcessBooking';
import * as inquiryProcess from './transactionProcessInquiry';
import * as negotiationProcess from './transactionProcessNegotiation';
import { toCanonicalTransitionName } from './transitionAliases';
import { isFrontendCanceledTransaction } from '../util/transactionProductionOverrides';

// Supported unit types
// Note: These are passed to translations/microcopy in certain cases.
//       Therefore, they can't contain wordbreaks like '-' or space ' '
export const ITEM = 'item';
export const DAY = 'day';
export const NIGHT = 'night';
export const HOUR = 'hour';
export const FIXED = 'fixed';
export const INQUIRY = 'inquiry';
export const OFFER = 'offer'; // The unitType 'offer' means that provider created the listing on default-negotiation process
export const REQUEST = 'request'; // The unitType 'request' means that customer created the listing on default-negotiation process

// Then names of supported processes
export const PURCHASE_PROCESS_NAME = 'default-purchase';
export const BOOKING_PROCESS_NAME = 'default-booking';
export const INQUIRY_PROCESS_NAME = 'default-inquiry';
export const NEGOTIATION_PROCESS_NAME = 'default-negotiation';

/**
 * A process should export:
 * - graph
 * - states
 * - transitions
 * - isRelevantPastTransition(transition)
 * - isPrivileged(transition)
 * - isCompleted(transition)
 * - isRefunded(transition)
 * - isCustomerReview(transition)
 * - isProviderReview(transition)
 * - statesNeedingCustomerAttention
 */
const PROCESSES = [
  {
    name: PURCHASE_PROCESS_NAME,
    alias: `${PURCHASE_PROCESS_NAME}/release-1`,
    process: purchaseProcess,
    unitTypes: [ITEM],
  },
  {
    name: BOOKING_PROCESS_NAME,
    alias: `${BOOKING_PROCESS_NAME}/release-1`,
    process: bookingProcess,
    unitTypes: [DAY, NIGHT, HOUR, FIXED],
  },
  {
    name: INQUIRY_PROCESS_NAME,
    alias: `${INQUIRY_PROCESS_NAME}/release-1`,
    process: inquiryProcess,
    unitTypes: [INQUIRY],
  },
  {
    name: NEGOTIATION_PROCESS_NAME,
    alias: `${NEGOTIATION_PROCESS_NAME}/release-1`,
    process: negotiationProcess,
    unitTypes: [OFFER, REQUEST],
  },
];

const LEGACY_BOOKING_PROCESS_NAME = 'booking-process';
const LINE_ITEM_ITEM = 'line-item/item';

const PURCHASE_LIFECYCLE_TRANSITIONS = new Set([
  purchaseProcess.transitions.REQUEST_BOOKING_WITHOUT_PAYOUT,
  purchaseProcess.transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_INQUIRY,
  purchaseProcess.transitions.REQUEST_BOOKING_WITHOUT_PAYOUT_AFTER_OFFER,
  purchaseProcess.transitions.CONFIRM_BOOKING_WITHOUT_PAYOUT,
  purchaseProcess.transitions.EXPIRE_PAYMENT_WITHOUT_PAYOUT,
  purchaseProcess.transitions.MARK_DELIVERED,
  purchaseProcess.transitions.MARK_DELIVERED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.OPERATOR_MARK_DELIVERED,
  purchaseProcess.transitions.OPERATOR_MARK_DELIVERED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.MARK_RECEIVED_FROM_PURCHASED,
  purchaseProcess.transitions.MARK_RECEIVED_FROM_PURCHASED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.MARK_RECEIVED,
  purchaseProcess.transitions.MARK_RECEIVED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.AUTO_MARK_RECEIVED,
  purchaseProcess.transitions.AUTO_MARK_RECEIVED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.DISPUTE,
  purchaseProcess.transitions.DISPUTE_WITHOUT_PAYOUT,
  purchaseProcess.transitions.OPERATOR_DISPUTE,
  purchaseProcess.transitions.OPERATOR_DISPUTE_WITHOUT_PAYOUT,
  purchaseProcess.transitions.AUTO_CANCEL,
  purchaseProcess.transitions.AUTO_CANCEL_WITHOUT_PAYOUT,
  purchaseProcess.transitions.AUTO_CANCEL_FROM_DISPUTED,
  purchaseProcess.transitions.AUTO_CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.CANCEL_FROM_DISPUTED,
  purchaseProcess.transitions.CANCEL_FROM_DISPUTED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.MARK_RECEIVED_FROM_DISPUTED,
  purchaseProcess.transitions.MARK_RECEIVED_FROM_DISPUTED_WITHOUT_PAYOUT,
  purchaseProcess.transitions.AUTO_COMPLETE,
  purchaseProcess.transitions.WALLET_UPDATED,
  purchaseProcess.transitions.UPDATE_WALLET_BALANCE,
  purchaseProcess.transitions.TRANSITION_SEND_OFFER,
  purchaseProcess.transitions.TRANSITION_ACCEPT_OFFER,
  purchaseProcess.transitions.TRANSITION_DECLINE_OFFER,
  purchaseProcess.transitions.TRANSITION_COUNTER_OFFER,
  purchaseProcess.transitions.TRANSITION_ACCEPT_AFTER_COUNTER_OFFER,
  purchaseProcess.transitions.TRANSITION_DECLINE_AFTER_COUNTER_OFFER,
  purchaseProcess.transitions.TRANSITION_AUTO_ACCEPT_OFFER,
  purchaseProcess.transitions.REQUEST_PAYMENT_AFTER_OFFER,
  purchaseProcess.transitions.REQUEST_PAYMENT_AFTER_OFFER_WITH_PAYPAL,
  purchaseProcess.transitions.TRANSITION_AUTO_EXPIRE_OFFER_PAYMENT,
]);

const extractBaseProcessName = processName =>
  typeof processName === 'string' && processName.includes('/') ? processName.split('/')[0] : processName;

const extractTransitionName = transitionOrEntity => {
  if (typeof transitionOrEntity === 'string') {
    return transitionOrEntity;
  }
  if (transitionOrEntity?.attributes?.name) {
    return transitionOrEntity.attributes.name;
  }
  if (transitionOrEntity?.transition) {
    return transitionOrEntity.transition;
  }
  return null;
};

const getTransactionUnitType = transaction => {
  const protectedUnitType = transaction?.attributes?.protectedData?.unitType;
  const listingUnitType = transaction?.listing?.attributes?.publicData?.unitType;
  const lineItemUnitType = transaction?.attributes?.lineItems?.some(
    lineItem => lineItem?.code === LINE_ITEM_ITEM
  )
    ? ITEM
    : null;

  return protectedUnitType || listingUnitType || lineItemUnitType || null;
};

const hasPurchaseLifecycleTransition = transitions =>
  transitions.some(transitionOrEntity => {
    const transitionName = extractTransitionName(transitionOrEntity);
    return PURCHASE_LIFECYCLE_TRANSITIONS.has(toCanonicalTransitionName(transitionName));
  });

/**
 * Helper functions to figure out if transaction is in a specific state.
 * State is based on lastTransition given by transaction object and state description.
 *
 * @param {Object} tx transaction entity
 */
const txLastTransition = tx => tx?.attributes?.lastTransition;

/**
 * Get states from the graph.
 *
 * Note: currently we assume that state description is in stateX format
 *       and it doesn't contain nested states.
 *
 * @param {Object} graph Description of transaction process graph in StateX format
 */
const statesObjectFromGraph = graph => graph.states || {};

/**
 * This is a helper function that's attached to exported 'getProcess'.
 * Get next process state after given transition.
 *
 * @param {Object} process imported from a separate file
 * @returns {function} Returns a function to check the next state after given transition.
 */
const getStateAfterTransition = process => transition => {
  const canonicalTransition = toCanonicalTransitionName(transition);
  const statesObj = statesObjectFromGraph(process.graph);
  const stateNames = Object.keys(statesObj);
  const fromState = stateNames.find(stateName => {
    const transitionsForward = Object.keys(statesObj[stateName]?.on || {});
    return transitionsForward.includes(canonicalTransition);
  });

  return fromState && canonicalTransition && statesObj[fromState]?.on[canonicalTransition]
    ? statesObj[fromState]?.on[canonicalTransition]
    : null;
};

/**
 * This is a helper function that's attached to exported 'getProcess' as 'getState'
 * Get state based on lastTransition of given transaction entity.
 *
 * How to use this function:
 *   // import { getProcess } from '../../transactions/transaction';
 *   const process = getProcess(processName);
 *   const state = process.getState(tx);
 *   const isInquiry = state === process.states.INQUIRY
 *
 * @param {Object} process imported from a separate file
 * @returns {function} Returns a function to check the current state of transaction entity against
 * given process.
 */
const getProcessState = process => tx => {
  // Temporary frontend-only production fallback: force this one transaction through the
  // existing canceled UI paths without mutating backend transaction data.
  if (isFrontendCanceledTransaction(tx) && process?.states?.CANCELED) {
    return process.states.CANCELED;
  }

  if (typeof process.resolveState === 'function') {
    const resolvedState = process.resolveState(tx);
    if (resolvedState) {
      return resolvedState;
    }
  }

  return getStateAfterTransition(process)(txLastTransition(tx));
};

/**
 * Pick transition names that lead to target state from given entries.
 *
 * First parameter, "transitionEntries", should look like this:
 * [
 *   [transitionForward1, stateY],
 *   [transitionForward2, stateY],
 *   [transitionForward3, stateZ],
 * ]
 *
 * @param {Array} transitionEntries
 * @param {String} targetState
 * @param {Array} initialTransitions
 */
const pickTransitionsToTargetState = (transitionEntries, targetState, initialTransitions) => {
  return transitionEntries.reduce((pickedTransitions, transitionEntry) => {
    const [transition, nextState] = transitionEntry;
    return nextState === targetState ? [...pickedTransitions, transition] : pickedTransitions;
  }, initialTransitions);
};

/**
 * Get all the transitions that lead to specified state.
 *
 * Process uses following syntax to describe the graph:
 * states: {
 *   stateX: {
 *     on: {
 *       transitionForward1: stateY,
 *       transitionForward2: stateY,
 *       transitionForward3: stateZ,
 *     },
 *   },
 *   stateY: {},
 *   stateZ: {
 *     on: {
 *       transitionForward4: stateY,
 *     },
 *   },
 * },
 *
 * Finding all the transitions to 'stateY' should pick transitions: 1, 2, 4
 *
 * @param {Object} process
 * @param {String} targetState
 */
const getTransitionsToState = (process, targetState) => {
  const states = Object.values(statesObjectFromGraph(process.graph));

  return states.reduce((collectedTransitions, inspectedState) => {
    const transitionEntriesForward = Object.entries(inspectedState.on || {});
    return pickTransitionsToTargetState(
      transitionEntriesForward,
      targetState,
      collectedTransitions
    );
  }, []);
};

/**
 * Transitions that lead to given states.
 *
 * @param {Object} process against which transitions and states are checked.
 * @returns {function} Returns a function to get the transitions that lead to given states.
 */
const getTransitionsToStates = process => stateNames => {
  return stateNames.reduce((pickedTransitions, stateName) => {
    return [...pickedTransitions, ...getTransitionsToState(process, stateName)];
  }, []);
};

/**
 * Helper functions to figure out if transaction has passed a given state.
 * This is based on transitions history given by transaction object.
 *
 * @param {Object} process against which passed states are checked.
 */
const hasPassedState = process => (stateName, tx) => {
  const txTransitions = tx => tx?.attributes?.transitions || [];
  const hasPassedTransition = (transitionName, tx) =>
    !!txTransitions(tx).find(t =>
      toCanonicalTransitionName(t.transition) === toCanonicalTransitionName(transitionName)
    );

  return (
    getTransitionsToState(process, stateName).filter(t => hasPassedTransition(t, tx)).length > 0
  );
};

/**
 * If process has been renamed, but the graph itself is the same,
 * this function allows referencing the updated name of the process.
 * ProcessName is used in some translation keys and stateData functions.
 *
 * Note: If the process graph has changed, you must create a separate process graph for it.
 *
 * @param {String} processName
 */
export const resolveLatestProcessName = processName => {
  const normalizedProcessName =
    typeof processName === 'string' && processName.includes('/')
      ? processName.split('/')[0]
      : processName;

  switch (normalizedProcessName) {
    case 'flex-product-default-process':
    case 'default-buying-products':
    case 'offer-process':
    case 'wallet-process':
    case PURCHASE_PROCESS_NAME:
      return PURCHASE_PROCESS_NAME;
    case 'flex-default-process':
    case 'flex-hourly-default-process':
    case 'flex-booking-default-process':
    case 'booking-process':
    case BOOKING_PROCESS_NAME:
      return BOOKING_PROCESS_NAME;
    case INQUIRY_PROCESS_NAME:
      return INQUIRY_PROCESS_NAME;
    case NEGOTIATION_PROCESS_NAME:
      return NEGOTIATION_PROCESS_NAME;
    default:
      return normalizedProcessName;
  }
};

/**
 * Get process based on process name
 * @param {String} processName
 */
export const getProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  if (processInfo) {
    const baseProcess = processInfo.process;
    const getCanonicalTransition = transitionName => toCanonicalTransitionName(transitionName);

    return {
      ...baseProcess,
      getState: getProcessState(baseProcess),
      getStateAfterTransition: getStateAfterTransition(baseProcess),
      getTransitionsToStates: getTransitionsToStates(baseProcess),
      hasPassedState: hasPassedState(baseProcess),
      isRelevantPastTransition: transition =>
        baseProcess.isRelevantPastTransition(getCanonicalTransition(transition)),
      isCustomerReview: transition => baseProcess.isCustomerReview(getCanonicalTransition(transition)),
      isProviderReview: transition => baseProcess.isProviderReview(getCanonicalTransition(transition)),
      isPrivileged: transition => baseProcess.isPrivileged(getCanonicalTransition(transition)),
      isCompleted: transition => baseProcess.isCompleted(getCanonicalTransition(transition)),
      isRefunded: transition => baseProcess.isRefunded(getCanonicalTransition(transition)),
    };
  } else {
    const error = new Error(`Unknown transaction process name: ${processName}`);
    console.error(error, 'unknown-transaction-process', { processName });
    throw error;
  }
};

/**
 * Check if the transaction belongs to the legacy booking-process alias.
 *
 * @param {Object} transaction
 */
export const isLegacyBookingProcessTransaction = transaction => {
  const originalProcessName = extractBaseProcessName(transaction?.attributes?.processName);
  return originalProcessName === LEGACY_BOOKING_PROCESS_NAME;
};

/**
 * Legacy booking-process transactions were previously used for product lifecycle flows.
 * Those transactions should use purchase lifecycle UI if they carry item semantics or
 * purchase-specific transitions.
 *
 * @param {Object} transaction
 * @param {Object} [options]
 * @param {Array} [options.nextTransitions]
 */
export const isLegacyBookingProcessPurchaseTransaction = (transaction, options = {}) => {
  if (!isLegacyBookingProcessTransaction(transaction)) {
    return false;
  }

  if (getTransactionUnitType(transaction) === ITEM) {
    return true;
  }

  const nextTransitions = Array.isArray(options?.nextTransitions) ? options.nextTransitions : [];
  const transitions = [
    transaction?.attributes?.lastTransition,
    ...(transaction?.attributes?.transitions || []),
    ...nextTransitions,
  ].filter(Boolean);

  return hasPurchaseLifecycleTransition(transitions);
};

/**
 * Resolve the effective process name for a transaction.
 * This keeps normal current transactions unchanged while allowing
 * legacy booking-process product transactions to use purchase lifecycle helpers.
 *
 * @param {Object} transaction
 * @param {Object} [options]
 * @param {Array} [options.nextTransitions]
 */
export const getTransactionProcessName = (transaction, options = {}) => {
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);

  if (
    processName === BOOKING_PROCESS_NAME &&
    isLegacyBookingProcessPurchaseTransaction(transaction, options)
  ) {
    return PURCHASE_PROCESS_NAME;
  }

  return processName;
};

/**
 * Resolve the effective process helper for a transaction.
 *
 * @param {Object} transaction
 * @param {Object} [options]
 * @param {Array} [options.nextTransitions]
 */
export const getTransactionProcess = (transaction, options = {}) => {
  const processName = getTransactionProcessName(transaction, options);
  return processName ? getProcess(processName) : null;
};

/**
 * Get the info about supported processes: name, alias, unitTypes
 */
export const getSupportedProcessesInfo = () =>
  PROCESSES.map(p => {
    const { process, ...rest } = p;
    return rest;
  });

/**
 * Get all the transitions for every supported process
 */
export const getAllTransitionsForEveryProcess = () => {
  return PROCESSES.reduce((accTransitions, processInfo) => {
    return [...accTransitions, ...Object.values(processInfo.process.transitions)];
  }, []);
};

/**
 * Check if the process is purchase process
 *
 * @param {String} processName
 */
export const isPurchaseProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [PURCHASE_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a booking process
 *
 * @param {String} processAlias
 */
export const isPurchaseProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isPurchaseProcess(processName) : false;
};

/**
 * Check if the process is booking process
 *
 * @param {String} processName
 */
export const isBookingProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [BOOKING_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a booking process
 *
 * @param {String} processAlias
 */
export const isBookingProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isBookingProcess(processName) : false;
};

/**
 * Check if the process is inquiry process
 *
 * @param {String} processName
 */
export const isInquiryProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [INQUIRY_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a inquiry process
 *
 * @param {String} processAlias
 */
export const isInquiryProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isInquiryProcess(processName) : false;
};

/**
 * Check if the process is negotiation process
 *
 * @param {String} processName
 */
export const isNegotiationProcess = processName => {
  const latestProcessName = resolveLatestProcessName(processName);
  const processInfo = PROCESSES.find(process => process.name === latestProcessName);
  return [NEGOTIATION_PROCESS_NAME].includes(processInfo?.name);
};

/**
 * Check if the process/alias points to a negotiation process
 *
 * @param {String} processAlias
 */
export const isNegotiationProcessAlias = processAlias => {
  const processName = processAlias ? processAlias.split('/')[0] : null;
  return processAlias ? isNegotiationProcess(processName) : false;
};

/**
 * Check from unit type if full days should be used.
 * E.g. unit type is day or night
 * This is mainly used for availability management.
 *
 * @param {String} unitType
 */
export const isFullDay = unitType => {
  return [DAY, NIGHT].includes(unitType);
};

/**
 * Get states that need provider's attention for every supported process
 */
export const getStatesNeedingProviderAttention = () => {
  return PROCESSES.reduce((accStates, processInfo) => {
    const statesNeedingProviderAttention = processInfo.process.statesNeedingProviderAttention || [];
    // Return only unique state names
    // TODO: this setup is subject to problems if one process has important state named
    // similarly as unimportant state in another process.
    return [...new Set([...accStates, ...statesNeedingProviderAttention])];
  }, []);
};

/**
 * Get states that need customer's attention for every supported process
 */
export const getStatesNeedingCustomerAttention = () => {
  return PROCESSES.reduce((accStates, processInfo) => {
    const statesNeedingCustomerAttention = processInfo.process.statesNeedingCustomerAttention || [];
    // Return only unique state names
    // TODO: this setup is subject to problems if one process has important state named
    // similarly as unimportant state in another process.
    return [...new Set([...accStates, ...statesNeedingCustomerAttention])];
  }, []);
};

/**
 * Actors
 *
 * There are 4 different actors that might initiate transitions:
 */

// Roles of actors that perform transaction transitions
export const TX_TRANSITION_ACTOR_CUSTOMER = 'customer';
export const TX_TRANSITION_ACTOR_PROVIDER = 'provider';
export const TX_TRANSITION_ACTOR_SYSTEM = 'system';
export const TX_TRANSITION_ACTOR_OPERATOR = 'operator';

export const TX_TRANSITION_ACTORS = [
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  TX_TRANSITION_ACTOR_SYSTEM,
  TX_TRANSITION_ACTOR_OPERATOR,
];

/**
 * Get the role of the current user on given transaction entity.
 *
 * @param {UUID} currentUserId UUID of the currentUser entity
 * @param {Object} transaction Transaction entity from Marketplace API
 */
export const getUserTxRole = (currentUserId, transaction) => {
  const customer = transaction?.customer;
  if (currentUserId && currentUserId.uuid && transaction?.id && customer.id) {
    // user can be either customer or provider
    return currentUserId.uuid === customer.id.uuid
      ? TX_TRANSITION_ACTOR_CUSTOMER
      : TX_TRANSITION_ACTOR_PROVIDER;
  } else {
    throw new Error(`Parameters for "userIsCustomer" function were wrong.
      currentUserId: ${currentUserId}, transaction: ${transaction}`);
  }
};

/**
 * Wildcard string for ConditionalResolver's conditions.
 */
export const CONDITIONAL_RESOLVER_WILDCARD = '*';

/**
 * This class helps to resolve correct UI data for each combination of conditional data [state & role]
 *
 * Usage:
 *  const stateData = new ConditionalResolver([currentState, currentRole])
 *    .cond(['inquiry', 'customer'], () => {
 *      return { showInfoX: true, isSomethingOn: true };
 *    })
 *    .cond(['purchase', _], () => {
 *      return { showInfoX: false, isSomethingOn: true };
 *    })
 *    .default(() => {
 *      return { showDetailCardHeadings: true };
 *    })
 *    .resolve();
 */
export class ConditionalResolver {
  constructor(data) {
    this.data = data;
    this.resolver = null;
    this.defaultResolver = null;
  }
  cond(conditions, resolver) {
    if (conditions?.length === this.data.length && this.resolver == null) {
      const isDefined = item => typeof item !== 'undefined';
      const isWildcard = item => item === CONDITIONAL_RESOLVER_WILDCARD;
      const isMatch = conditions.reduce(
        (isPartialMatch, item, i) =>
          isPartialMatch && isDefined(item) && (isWildcard(item) || item === this.data[i]),
        true
      );
      this.resolver = isMatch ? resolver : null;
    }
    return this;
  }
  default(defaultResolver) {
    this.defaultResolver = defaultResolver;
    return this;
  }
  resolve() {
    // This resolves the output against current conditions.
    // Therefore, call for resolve() must be the last call in method chain.
    return this.resolver ? this.resolver() : this.defaultResolver ? this.defaultResolver() : null;
  }
}
